import { useEffect, useState } from "react";
import { GraduationCap, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

/**
 * PlacementStrip — a slim strip on the Outcomes tab showing the share
 * of a uni's grads continuing to a top-30 grad school. Hides
 * gracefully when we don't have curated data.
 */
export default function PlacementStrip({ uniId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/universities/${uniId}/placement-rate`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [uniId]);

  const p = data?.placement;
  if (!p) return null;

  const pct = p.top_grad_school_pct;
  const tone = pct >= 45 ? "#059669" : pct >= 25 ? "#7C3AED" : "#B45309";

  return (
    <div
      className="flex items-center justify-between gap-4 p-4 md:p-5 mb-6 border border-[#002147]/10 bg-white/40"
      data-testid="placement-strip"
    >
      <div className="flex items-start gap-3 min-w-0">
        <GraduationCap className="h-4 w-4 mt-0.5 shrink-0" style={{ color: tone }} />
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] mb-1"
               style={{ color: tone }}>
            To top-30 grad school
          </div>
          <div className="font-sans text-sm text-[#002147]/80 leading-relaxed">
            {p.context}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4 shrink-0">
        <div className="text-right">
          <div className="font-serif text-3xl md:text-4xl leading-none tabular-nums" style={{ color: tone }}>
            {pct}%
          </div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-[#002147]/50 mt-1">
            of grads
          </div>
        </div>
        {p.source_url && (
          <a
            href={p.source_url}
            target="_blank" rel="noopener noreferrer"
            className="font-mono text-[10px] uppercase tracking-widest text-[#7C3AED] hover:underline inline-flex items-center gap-1"
          >
            Careers <ExternalLink className="h-2.5 w-2.5" />
          </a>
        )}
      </div>
    </div>
  );
}
